import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { Alert, Badge, Spinner, Table } from 'reactstrap';
import { Button } from 'primereact/button';
import firebase from '@/config/firebase.config';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { TabBar } from '@/components/TabBar';
import styles from './InvitationsHub.module.scss';
import { fetchManagedTeamIdsForCompany } from '@/services/userMembership';
import {
  acceptInviteFromInvites,
  declineInviteFromInvites,
  revokeInvite,
  resendInvite,
} from '@/services/invitesClient';
import { addNotification, notificationStates } from '@/components/Notification';
import { authActionTypes } from '@/store/Auth/Auth.actionTypes';
import { queryKeys } from '@/config/queryKeys';
import { teamActionTypes } from '@/store/Team/Team.actionTypes';

const database = firebase.firestore();

const TAB_MY = 'myInvites';
const TAB_SENT = 'sentInvites';

function formatTimestamp(value) {
  if (value == null) return '—';
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate().toLocaleString();
    } catch {
      return '—';
    }
  }
  if (typeof value === 'object' && value.seconds != null) {
    return new Date(value.seconds * 1000).toLocaleString();
  }
  return '—';
}

function scopeLabel(data) {
  if (data.scopeType === 'company_admin') {
    return 'Company (Admin)';
  }
  return data.teamName || data.contextTeamName || data.teamId || '—';
}

function statusBadgeVariant(status) {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'accepted':
      return 'success';
    case 'declined':
      return 'info';
    case 'expired':
      return 'secondary';
    case 'revoked':
      return 'danger';
    default:
      return 'light';
  }
}

export const InvitationsHubComponent = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const userProfile = useSelector((state) => state.auth.profile);
  const companyId = userProfile?.activeCompany?.companyId;
  const companyName = userProfile?.activeCompany?.companyName;
  const role = userProfile?.activeCompany?.role;

  const [activeTab, setActiveTab] = useState(TAB_MY);
  const [myInvites, setMyInvites] = useState([]);
  const [sentInvites, setSentInvites] = useState([]);
  const [loadingMy, setLoadingMy] = useState(true);
  const [loadingSent, setLoadingSent] = useState(true);
  const [error, setError] = useState(null);
  const [managedTeamIds, setManagedTeamIds] = useState([]);
  const [managedTeamsReady, setManagedTeamsReady] = useState(
    () => userProfile?.activeCompany?.role !== 'Manager'
  );
  const [actionLoadingIds, setActionLoadingIds] = useState(new Set());
  const [confirmRevokeId, setConfirmRevokeId] = useState(null);

  const addLoading = (id) =>
    setActionLoadingIds((prev) => new Set([...prev, id]));
  const removeLoading = (id) =>
    setActionLoadingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const showSentTab = !!companyId && (role === 'Admin' || role === 'Manager');
  const userEmail = useMemo(() => {
    const u = firebase.auth().currentUser;
    const fromAuth = u?.email?.toLowerCase().trim();
    if (fromAuth) return fromAuth;
    return userProfile?.email?.toLowerCase().trim() || null;
  }, [userProfile?.id, userProfile?.email]);

  useEffect(() => {
    if (!userProfile?.id || !companyId) {
      setManagedTeamIds([]);
      setManagedTeamsReady(true);
      return;
    }
    if (role !== 'Manager') {
      setManagedTeamIds([]);
      setManagedTeamsReady(true);
      return;
    }
    setManagedTeamsReady(false);
    let cancelled = false;
    fetchManagedTeamIdsForCompany(userProfile.id, companyId).then((res) => {
      if (cancelled) return;
      setManagedTeamIds(res.success ? res.data || [] : []);
      setManagedTeamsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [userProfile?.id, companyId, role]);

  useEffect(() => {
    if (!userEmail) {
      setMyInvites([]);
      setLoadingMy(false);
      return;
    }
    setLoadingMy(true);
    setError(null);
    const q = database
      .collection('invites')
      .where('inviteeEmail', '==', userEmail)
      .orderBy('createdAt', 'desc')
      .limit(100);

    const unsub = q.onSnapshot(
      (snap) => {
        const rows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setMyInvites(rows);
        setLoadingMy(false);
      },
      (err) => {
        console.error('[InvitationsHub] my invites', err);
        setError(err.message || 'Could not load your invitations.');
        setMyInvites([]);
        setLoadingMy(false);
      }
    );
    return () => unsub();
  }, [userEmail]);

  useEffect(() => {
    if (!companyId || !showSentTab) {
      setSentInvites([]);
      setLoadingSent(false);
      return;
    }
    if (role === 'Manager' && !managedTeamsReady) {
      setLoadingSent(true);
      return;
    }
    setLoadingSent(true);
    const q = database
      .collection('invites')
      .where('companyId', '==', companyId)
      .orderBy('createdAt', 'desc')
      .limit(200);

    const unsub = q.onSnapshot(
      (snap) => {
        let rows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        if (role === 'Manager') {
          rows = rows.filter((r) => {
            if (r.scopeType === 'company_admin') return false;
            return (
              r.scopeType === 'team' &&
              r.teamId &&
              managedTeamIds.includes(r.teamId)
            );
          });
        }
        setSentInvites(rows);
        setLoadingSent(false);
      },
      (err) => {
        console.error('[InvitationsHub] sent invites', err);
        setError(err.message || 'Could not load sent invitations.');
        setSentInvites([]);
        setLoadingSent(false);
      }
    );
    return () => unsub();
  }, [companyId, showSentTab, role, managedTeamIds, managedTeamsReady]);

  const tabOptions = useMemo(() => {
    const base = [{ label: 'My invites', value: TAB_MY }];
    if (showSentTab) {
      base.push({ label: 'Sent invites', value: TAB_SENT });
    }
    return base;
  }, [showSentTab]);

  useEffect(() => {
    if (!showSentTab && activeTab === TAB_SENT) {
      setActiveTab(TAB_MY);
    }
  }, [showSentTab, activeTab]);

  const handleAcceptInvite = async (inviteId) => {
    const user = firebase.auth().currentUser;
    if (!user) {
      addNotification({
        message: 'Please sign in again.',
        level: notificationStates.error,
      });
      return;
    }
    addLoading(inviteId);
    try {
      const res = await acceptInviteFromInvites(inviteId, null, user);
      if (res.success) {
        if (!userProfile?.activeCompany?.companyId && res.companyId) {
          localStorage.setItem('cid', res.companyId);
        }
        dispatch({ type: authActionTypes.LOAD_CURRENT_ACCOUNT });
        if (res.teamPayload) {
          dispatch({
            type: teamActionTypes.SET_SELECT_TEAM_ID,
            payload: res.teamPayload,
          });
        }
        if (userProfile?.id) {
          queryClient.invalidateQueries(queryKeys.users.detail(userProfile.id));
        }
        addNotification({
          message: 'Invitation accepted.',
          level: notificationStates.success,
        });
      } else {
        addNotification({
          message: res.message || 'Could not accept.',
          level: notificationStates.error,
        });
      }
    } catch (e) {
      addNotification({
        message: e?.message || 'Could not accept.',
        level: notificationStates.error,
      });
    } finally {
      removeLoading(inviteId);
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    addLoading(inviteId);
    try {
      const res = await declineInviteFromInvites(inviteId, null);
      if (res.success) {
        addNotification({
          message: 'Invitation declined.',
          level: notificationStates.info,
        });
      } else {
        addNotification({
          message: res.message || 'Could not decline.',
          level: notificationStates.error,
        });
      }
    } catch (e) {
      addNotification({
        message: e?.message || 'Could not decline.',
        level: notificationStates.error,
      });
    } finally {
      removeLoading(inviteId);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    addLoading(inviteId);
    setConfirmRevokeId(null);
    try {
      const res = await revokeInvite(inviteId);
      if (res.success) {
        addNotification({
          message: 'Invitation revoked.',
          level: notificationStates.success,
        });
      } else {
        addNotification({
          message: res.message || 'Could not revoke.',
          level: notificationStates.error,
        });
      }
    } catch (e) {
      addNotification({
        message: e?.message || 'Could not revoke.',
        level: notificationStates.error,
      });
    } finally {
      removeLoading(inviteId);
    }
  };

  const handleResendInvite = async (inviteId) => {
    addLoading(inviteId);
    try {
      const res = await resendInvite(inviteId);
      if (res.success) {
        addNotification({
          message: 'Invitation resent successfully.',
          level: notificationStates.success,
        });
      } else {
        addNotification({
          message: res.message || 'Could not resend.',
          level: notificationStates.error,
        });
      }
    } catch (e) {
      addNotification({
        message: e?.message || 'Could not resend.',
        level: notificationStates.error,
      });
    } finally {
      removeLoading(inviteId);
    }
  };

  return (
    <div className={styles.invitationsHubPage}>
      <Text
        variant={textVariantsEnum.Header}
        fontWeight={fontWeightEnum.Semibold}
      >
        Invitations
      </Text>
      <p className={`mt-2 mb-0 ${styles.muted}`}>
        {!companyId ? (
          <span>
            Invitations sent to your email. Accept or decline below — no company
            selection required.
          </span>
        ) : null}
        {companyName ? (
          <>
            {companyId ? ' ' : null}
            Company: <strong>{companyName}</strong>
          </>
        ) : null}
        {showSentTab && activeTab === TAB_SENT && role === 'Manager' && (
          <span className="d-block mt-1">
            Showing invitations for teams you manage.
          </span>
        )}
      </p>

      <div className={styles.panel}>
        <div className={styles.tabBarWrap}>
          <TabBar
            tabs={tabOptions}
            activeTab={activeTab}
            onTabClick={setActiveTab}
          />
        </div>

        {error && (
          <Alert color="danger" className="w-100">
            {error}
          </Alert>
        )}

        {activeTab === TAB_MY && (
          <div className={styles.tableWrap}>
            {loadingMy ? (
              <div className="py-5 text-center">
                <Spinner color="primary" />
              </div>
            ) : myInvites.length === 0 ? (
              <p className={styles.muted}>No invitations for your email yet.</p>
            ) : (
              <Table responsive striped borderless size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th className={styles.cellLeft}>Status</th>
                    <th className={styles.cellLeft}>Company</th>
                    <th className={styles.cellLeft}>Scope</th>
                    <th className={styles.cellLeft}>Role</th>
                    <th className={styles.cellLeft}>Sent</th>
                    <th className={styles.cellLeft}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {myInvites.map((row) => (
                    <tr key={row.id}>
                      <td className={styles.cellLeft}>
                        <Badge color={statusBadgeVariant(row.status)}>
                          {row.status || '—'}
                        </Badge>
                      </td>
                      <td className={styles.cellLeft}>
                        {row.companyName || '—'}
                      </td>
                      <td className={styles.cellLeft}>{scopeLabel(row)}</td>
                      <td className={styles.cellLeft}>
                        {row.invitedAs || '—'}
                      </td>
                      <td className={styles.cellLeft}>
                        {formatTimestamp(row.createdAt)}
                      </td>
                      <td className={styles.cellLeft}>
                        {row.status === 'pending' ? (
                          <div
                            className="d-flex flex-wrap align-items-center"
                            style={{ gap: '0.5rem' }}
                          >
                            <Button
                              type="button"
                              label="Decline"
                              className="p-button-sm p-button-outlined p-button-danger"
                              disabled={actionLoadingIds.has(row.id)}
                              loading={actionLoadingIds.has(row.id)}
                              onClick={() => handleDeclineInvite(row.id)}
                            />
                            <Button
                              type="button"
                              label="Accept"
                              className="p-button-sm p-button-primary"
                              disabled={actionLoadingIds.has(row.id)}
                              loading={actionLoadingIds.has(row.id)}
                              onClick={() => handleAcceptInvite(row.id)}
                            />
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        )}

        {activeTab === TAB_SENT && showSentTab && (
          <div className={styles.tableWrap}>
            {loadingSent ? (
              <div className="py-5 text-center">
                <Spinner color="primary" />
              </div>
            ) : sentInvites.length === 0 ? (
              <p className={styles.muted}>No invitations sent for this view.</p>
            ) : (
              <Table responsive striped borderless size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th className={styles.cellLeft}>Status</th>
                    <th className={styles.cellLeft}>Invitee</th>
                    <th className={styles.cellLeft}>Scope</th>
                    <th className={styles.cellLeft}>Role</th>
                    <th className={styles.cellLeft}>Sent by</th>
                    <th className={styles.cellLeft}>Sent</th>
                    <th className={styles.cellLeft}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sentInvites.map((row) => (
                    <tr key={row.id}>
                      <td className={styles.cellLeft}>
                        <Badge color={statusBadgeVariant(row.status)}>
                          {row.status || '—'}
                        </Badge>
                      </td>
                      <td className={styles.cellLeft}>
                        <div>{row.inviteeEmail || '—'}</div>
                        {row.inviteeName ? (
                          <div className={styles.muted}>{row.inviteeName}</div>
                        ) : null}
                      </td>
                      <td className={styles.cellLeft}>{scopeLabel(row)}</td>
                      <td className={styles.cellLeft}>
                        {row.invitedAs || '—'}
                      </td>
                      <td className={styles.cellLeft}>
                        {row.invitedByEmail || '—'}
                      </td>
                      <td className={styles.cellLeft}>
                        {formatTimestamp(row.createdAt)}
                      </td>
                      <td className={styles.cellLeft}>
                        {row.status === 'pending' ? (
                          confirmRevokeId === row.id ? (
                            <div
                              className="d-flex flex-wrap align-items-center"
                              style={{ gap: '0.5rem' }}
                            >
                              <small className={styles.muted}>
                                Revoke invitation?
                              </small>
                              <Button
                                type="button"
                                label="Cancel"
                                className="p-button-sm p-button-outlined p-button-secondary"
                                disabled={actionLoadingIds.has(row.id)}
                                onClick={() => setConfirmRevokeId(null)}
                              />
                              <Button
                                type="button"
                                label="Confirm"
                                className="p-button-sm p-button-danger"
                                loading={actionLoadingIds.has(row.id)}
                                disabled={actionLoadingIds.has(row.id)}
                                onClick={() => handleRevokeInvite(row.id)}
                              />
                            </div>
                          ) : (
                            <div
                              className="d-flex flex-wrap align-items-center"
                              style={{ gap: '0.5rem' }}
                            >
                              <Button
                                type="button"
                                label="Resend"
                                icon="pi pi-refresh"
                                className="p-button-sm p-button-outlined p-button-secondary"
                                loading={actionLoadingIds.has(row.id)}
                                disabled={actionLoadingIds.has(row.id)}
                                onClick={() => handleResendInvite(row.id)}
                              />
                              <Button
                                type="button"
                                label="Revoke"
                                icon="pi pi-ban"
                                className="p-button-sm p-button-outlined p-button-danger"
                                disabled={actionLoadingIds.has(row.id)}
                                onClick={() => setConfirmRevokeId(row.id)}
                              />
                            </div>
                          )
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
